import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Toast from '../../../components/Toast/Toast'
import {
  searchChatsByPhone,
  getMessagesByPhonePaginated,
  deleteChatByPhone,
  sendMessageByPhone,
} from '../../../services/whatsappMaster'

const accent = '#01A76A'
const accentLight = 'rgba(1,167,106,0.08)'

const WA_CHATS = 'wm-chats'
const WA_MESSAGES = 'wm-messages'

const TYPE_CHAT_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: '1', label: 'Entrevista' },
  { value: '2', label: 'Prospección' },
  { value: '3', label: 'Empresas' },
]

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ─── Chat list item ───────────────────────────────────────────────────────────

const ChatItem = ({ chat, isSelected, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: '12px 16px',
      borderBottom: '1px solid #f5f5f5',
      cursor: 'pointer',
      background: isSelected ? accentLight : 'transparent',
      borderLeft: `3px solid ${isSelected ? accent : 'transparent'}`,
      transition: 'background 0.15s',
    }}
  >
    <div className="d-flex justify-content-between align-items-center mb-1">
      <span style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>
        {chat.phone}
      </span>
      {chat.types_chat_w && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            background: accentLight,
            color: accent,
            border: `1px solid ${accent}30`,
            borderRadius: 20,
            padding: '1px 8px',
          }}
        >
          {chat.types_chat_w.name}
        </span>
      )}
    </div>
    {chat.talent?.user && (
      <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>
        {chat.talent.user.names} {chat.talent.user.surnames}
      </div>
    )}
    {chat.lastMessageText && (
      <div
        style={{
          fontSize: 11,
          color: '#999',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 280,
        }}
      >
        {chat.lastMessageText}
      </div>
    )}
    {chat.lastMessageDate && (
      <div style={{ fontSize: 10, color: '#bbb', marginTop: 3 }}>
        {formatDate(chat.lastMessageDate)}
      </div>
    )}
  </div>
)

// ─── Message bubble ───────────────────────────────────────────────────────────

const SENDER = {
  bot: 'bot',
  master: 'master',
  user: 'user',
}

const SENDER_CONFIG = {
  [SENDER.bot]: {
    label: '🤖 Bot',
    align: 'align-items-end',
    bg: '#dcf8c6',
    border: '#b2dfdb',
    radius: '12px 12px 2px 12px',
  },
  [SENDER.master]: {
    label: '🛡️ Master',
    align: 'align-items-end',
    bg: '#e8f0fe',
    border: '#aec6f6',
    radius: '12px 12px 2px 12px',
  },
  [SENDER.user]: {
    label: '👤 Usuario',
    align: 'align-items-start',
    bg: '#fff',
    border: '#e8e8e8',
    radius: '12px 12px 12px 2px',
  },
}

const getSender = (msg) => {
  const shippingName = msg?.types_shipping_whatsapp?.name
  if (shippingName === 'user_hutrit') return SENDER.master
  if (shippingName === 'bot') return SENDER.bot
  if (msg?.from_hutrit === true || msg?.direction === 'outgoing' || msg?.fromMe === true)
    return SENDER.bot
  return SENDER.user
}

const MessageBubble = ({ msg }) => {
  const sender = getSender(msg)
  const config = SENDER_CONFIG[sender]

  const text =
    typeof msg?.text === 'string'
      ? msg.text
      : msg?.text?.body ?? msg?.message ?? msg?.body ?? ''

  const rawDate = msg?.date ?? msg?.timestamp
  const parsedDate = rawDate
    ? new Date(typeof rawDate === 'number' ? rawDate * 1000 : rawDate)
    : null

  return (
    <div className={`d-flex flex-column ${config.align}`}>
      <span style={{ fontSize: 10, color: '#aaa', marginBottom: 2, marginLeft: 4, marginRight: 4 }}>
        {config.label}
      </span>
      <div
        style={{
          maxWidth: '70%',
          background: config.bg,
          border: `1px solid ${config.border}`,
          borderRadius: config.radius,
          padding: '8px 12px',
          fontSize: 13,
          color: '#333',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        }}
      >
        {text}
        {parsedDate && (
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 4, textAlign: 'right' }}>
            {parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const WhatsappMaster = () => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [typeChat, setTypeChat] = useState('')
  const [selectedChat, setSelectedChat] = useState(null)
  const [page, setPage] = useState(0)
  const [messageInput, setMessageInput] = useState('')

  const { data: chatsData, isLoading: loadingChats } = useQuery(
    [WA_CHATS, search, typeChat],
    () => searchChatsByPhone({ talentSearch: search, typeChat, phone: search }),
    { keepPreviousData: true }
  )
  const chats = chatsData?.result ?? []

  const { data: messagesData, isLoading: loadingMessages } = useQuery(
    [WA_MESSAGES, selectedChat?.phone, page],
    () =>
      getMessagesByPhonePaginated({
        page,
        phone: selectedChat?.phone ?? '',
        talentId: selectedChat?.talent?.id ?? '',
      }),
    {
      enabled: !!selectedChat,
      keepPreviousData: true,
      refetchInterval: (data) => {
        const list = data?.data ?? []
        if (list.length < 2) return 30000
        const sorted = [...list].sort((a, b) => {
          const da = new Date(a.date ?? a.timestamp ?? 0)
          const db = new Date(b.date ?? b.timestamp ?? 0)
          return db - da
        })
        const latest = new Date(sorted[0].date ?? sorted[0].timestamp ?? 0)
        const second = new Date(sorted[1].date ?? sorted[1].timestamp ?? 0)
        const diffSeconds = (latest - second) / 1000
        return diffSeconds <= 60 ? 5000 : 30000
      },
    }
  )
  const messages = messagesData?.data ?? []

  const { mutate: deleteChat, isLoading: deleting } = useMutation(
    (phone) => deleteChatByPhone({ phone }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([WA_CHATS])
        setSelectedChat(null)
        Toast({ title: 'Chat eliminado', icon: 'info' })
      },
    }
  )

  const { mutate: sendMessage, isLoading: sending } = useMutation(
    ({ phone, message }) => sendMessageByPhone({ phone, message }),
    {
      onSuccess: () => {
        setMessageInput('')
        queryClient.invalidateQueries([WA_MESSAGES, selectedChat?.phone])
        Toast({ title: 'Mensaje enviado' })
      },
    }
  )

  const handleSend = (e) => {
    e.preventDefault()
    if (!messageInput.trim() || !selectedChat) return
    sendMessage({ phone: selectedChat.phone, message: messageInput.trim() })
  }

  const handleSelectChat = (chat) => {
    setSelectedChat(chat)
    setPage(0)
  }

  const handleDelete = () => {
    if (!selectedChat) return
    if (window.confirm(`¿Eliminar el chat con ${selectedChat.phone}?`)) {
      deleteChat(selectedChat.phone)
    }
  }

  return (
    <DashboardWrapper dashboardWrapperClassName='w-100'>
      {/* Header */}
      <div
        className="pt-5 mt-5"
        style={{
          background: 'linear-gradient(135deg, #01A76A 0%, #019960 100%)',
          borderRadius: 14,
          padding: '24px 28px',
          marginBottom: 24,
          color: '#fff',
        }}
      >
        <h4 style={{ fontWeight: 700, margin: 0 }}>Chat WhatsApp</h4>
        <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: 14 }}>
          Gestiona los chats de WhatsApp del sistema.
        </p>
      </div>

      {/* Body */}
      <div className="d-flex gap-3" style={{ height: 'calc(100vh - 270px)', minHeight: 400 }}>
        {/* Left — chat list */}
        <div
          style={{
            width: 340,
            flexShrink: 0,
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e8ecf0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Filters */}
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              className="form-control form-control-sm mb-2"
              style={{ borderRadius: 8, fontSize: 13 }}
              placeholder="Buscar por teléfono..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setSelectedChat(null)
              }}
            />
            <select
              className="form-control form-control-sm"
              style={{ borderRadius: 8, fontSize: 13 }}
              value={typeChat}
              onChange={(e) => {
                setTypeChat(e.target.value)
                setSelectedChat(null)
              }}
            >
              {TYPE_CHAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingChats ? (
              <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                Cargando chats...
              </div>
            ) : chats.length === 0 ? (
              <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                No hay chats
              </div>
            ) : (
              chats.map((chat) => (
                <ChatItem
                  key={chat.id}
                  chat={chat}
                  isSelected={selectedChat?.id === chat.id}
                  onClick={() => handleSelectChat(chat)}
                />
              ))
            )}
          </div>
        </div>

        {/* Right — messages */}
        <div
          style={{
            flex: 1,
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e8ecf0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {!selectedChat ? (
            <div
              className="d-flex align-items-center justify-content-center w-100 h-100 text-muted"
              style={{ fontSize: 14 }}
            >
              Selecciona un chat para ver los mensajes
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0' }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
                    {selectedChat.phone}
                  </div>
                  {selectedChat.talent?.user && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      {selectedChat.talent.user.names} {selectedChat.talent.user.surnames}
                      {' · '}
                      <span style={{ color: '#999' }}>{selectedChat.talent.user.email}</span>
                    </div>
                  )}
                  {selectedChat.types_chat_w && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        background: accentLight,
                        color: accent,
                        border: `1px solid ${accent}30`,
                        borderRadius: 20,
                        padding: '1px 8px',
                        display: 'inline-block',
                        marginTop: 4,
                      }}
                    >
                      {selectedChat.types_chat_w.name}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{
                    background: 'none',
                    border: '1px solid #ffcdd2',
                    borderRadius: 8,
                    padding: '6px 16px',
                    fontSize: 12,
                    color: '#e53935',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Eliminar chat
                </button>
              </div>

              {/* Messages area */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {loadingMessages ? (
                  <div className="text-center text-muted" style={{ fontSize: 13 }}>
                    Cargando mensajes...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted" style={{ fontSize: 13 }}>
                    No hay mensajes
                  </div>
                ) : (
                  [...messages].reverse().map((msg, i) => <MessageBubble key={msg.id ?? i} msg={msg} />)
                )}
              </div>

              {/* Send message */}
              <form
                onSubmit={handleSend}
                className="d-flex align-items-center gap-2"
                style={{ padding: '10px 20px', borderTop: '1px solid #f0f0f0' }}
              >
                <input
                  className="form-control"
                  style={{ borderRadius: 8, fontSize: 13 }}
                  placeholder="Escribe un mensaje..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={sending || !messageInput.trim()}
                  style={{
                    background: accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '7px 18px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sending ? '...' : 'Enviar'}
                </button>
              </form>

              {/* Pagination */}
              <div
                className="d-flex justify-content-center align-items-center gap-3"
                style={{ padding: '10px 20px', borderTop: '1px solid #f0f0f0' }}
              >
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  style={{
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    padding: '4px 14px',
                    fontSize: 12,
                    cursor: page === 0 ? 'not-allowed' : 'pointer',
                    color: '#555',
                  }}
                >
                  ← Anterior
                </button>
                <span style={{ fontSize: 12, color: '#999' }}>Página {page + 1}</span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  style={{
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    padding: '4px 14px',
                    fontSize: 12,
                    cursor: 'pointer',
                    color: '#555',
                  }}
                >
                  Siguiente →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardWrapper>
  )
}

export default WhatsappMaster
